import React, { useEffect, useMemo, useState } from "react";
import { SyncChannelManagerProducts } from "../../api/services/pms";
import { Button, Form, Icon, Label, Modal, Message } from "semantic-ui-react";
import { useTextResources, useMessageResources } from "../../language";
import PropTypes from "prop-types";
import { onError, onSuccess, onWarning } from "../shared/messages";
import { useConfirm } from "../../hooks/useConfirm";
import { DatePicker } from "../shared/DatePicker";
import moment from "moment";
import WeekDaysSelect from "../shared/WeekDaysSelect";
import RoomTypesDropDown from "../shared/RoomTypesDropDown";
import { dateFormat, InventoryReflectionTypesEnum } from "../shared/constants";
import NumericInput from "../shared/NumericInput";
import AllocationsDropDown from "../shared/AllocationsDropDown";
import { AllocationAndStrategy, RoomTypes, Availability } from "../../api/services/next";
import { getDaysOfDatesRange, isEmptyObject, toServerDateFormat } from "../shared/utils";
import { useFormFieldLabel } from "../../hooks/useFormFieldLabel";
import HotelsDropDown from "../shared/HotelsDropDown";
import { useLoader } from "../../hooks/useLoader";
import { isMobileOnly } from 'mobile-device-detect';
export const AllocationDatesPopup = ({ open, onClose, onSave, selectedAllocation, hotelId, isPerReservation = null, initialDate }) => {
  const loader = useLoader();
  const text = useTextResources();
  const message = useMessageResources();
  const label = useFormFieldLabel();
  const confirm = useConfirm();
  const [selectedHotel, setSelectedHotel] = useState(hotelId)
  const [allocation, setAllocation] = useState({});
  const [roomTypesData, setRoomTypesData] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [dates, setDates] = useState([]);
  const [days, setDays] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [allocatedRooms, setAllocatedRooms] = useState(null);
  const [topUp, setTopUp] = useState(null);
  const [roomsToKeep, setRoomsToKeep] = useState(null);
  const [minimumForInventoryReflection, setMinimumForInventoryReflection] = useState(null);
  const [maxRooms, setMaxRooms] = useState(null);
  const [releaseDays, setReleaseDays] = useState(null);
  const [leftRooms, setLeftRooms] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [maxAvailabilityDate, setMaxAvailabilityDate] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Wrapped setters to track dirty state
  const handleSetAllocatedRooms = (value) => { setAllocatedRooms(value); setIsDirty(true); };
  const handleSetTopUp = (value) => { setTopUp(value); setIsDirty(true); };
  const handleSetMaxRooms = (value) => { setMaxRooms(value); setIsDirty(true); };
  const handleSetRoomsToKeep = (value) => { setRoomsToKeep(value); setIsDirty(true); };
  const handleSetMinimumForInventoryReflection = (value) => { setMinimumForInventoryReflection(value); setIsDirty(true); };
  const handleSetRoomTypes = (value) => { setRoomTypes(value); setIsDirty(true); };
  const handleSetDays = (value) => { setDays(value); setIsDirty(true); };
  const handleSetReleaseDays = (value) => { setReleaseDays(value); setIsDirty(true); };
  const handleSetLeftRooms = (value) => { setLeftRooms(value); setIsDirty(true); };

  useEffect(() => {
    if (!open || !selectedAllocation) {
      setAllocation({});
      setSelectedDates([]);
      if (initialDate) {
        const clamped = moment.max(moment(initialDate), moment().startOf("day")).toDate();
        setDates([clamped, clamped]);
      } else {
        setDates([]);
      }
      setDays([]);
      setRoomTypes([]);
      setAllocatedRooms(null);
      setTopUp(null);
      setRoomsToKeep(null);
      setMinimumForInventoryReflection(null);
      setMaxRooms(null);
      setReleaseDays(null);
      setLeftRooms(null);
      setSelectedHotel(hotelId);
      setIsDirty(false);
    }
    else {
      setAllocation(selectedAllocation);
      setSelectedHotel(selectedAllocation.HotelId);
      if (selectedAllocation?.IsSharedInventory) {
        setRoomTypes(selectedAllocation?.RoomTypeIds)
      }
    }
  }, [selectedAllocation, open, hotelId, initialDate]);

  useEffect(() => {
      (async () => {
          if (selectedHotel) {
            var data = await RoomTypes.GetRoomTypes({ HotelId: selectedHotel });
            setRoomTypesData(data);
            
            try {
              const maxDate = await Availability.GetHotelLastAvailabilityDate({ hotelId: selectedHotel });
              setMaxAvailabilityDate(maxDate ? new Date(maxDate) : null);
            } catch (error) {
              console.error("Error fetching max availability date:", error);
              setMaxAvailabilityDate(null);
            }
          }
        })()
  }, [selectedHotel]);

  const hotelsSpec = useMemo(() => ({ HotelId: selectedHotel, GetHeadersOnly: true }), [selectedHotel]);
  const allocationsSpec = useMemo(() => ({ ...hotelsSpec, IsStrategy: false, IsPerReservation: isPerReservation }), [hotelsSpec, isPerReservation]);
  const roomTypeSpec = useMemo(() => ({ HotelId: selectedHotel, AllocationId: allocation?.Id }), [allocation, selectedHotel]);

  const saveAction = async () => {
    if (validate()) {
      setIsLoading(true);
      try {
        const dateRanges = selectedDates.length > 0 ? selectedDates : [dates];
        const dateList = dateRanges.map(pair => getDaysOfDatesRange(pair[0], pair[1], days)).flat();
        const allocationDates = [];
        dateList.forEach(date => {
          if (allocation.HotelLevelAllocation)
            allocationDates.push({
              AllocationId: allocation?.Id,
              RoomTypeId: null,
              Date: toServerDateFormat(date),
              AllocatedRooms: allocatedRooms,
              MaximumRooms: maxRooms,
              TopUpRooms: topUp,
              ExcludedRooms: roomsToKeep,
              MinimumForInventoryReflection: minimumForInventoryReflection,
              ReleaseDays: releaseDays,
              AppliedLeftRooms: leftRooms
            });
          else
            (allocation.IsSharedInventory ? allocation.RoomTypeIds : roomTypes).forEach(roomType => allocationDates.push({
              AllocationId: allocation?.Id,
              RoomTypeId: roomType,
              Date: toServerDateFormat(date),
              AllocatedRooms: allocatedRooms,
              MaximumRooms: maxRooms,
              TopUpRooms: topUp,
              ExcludedRooms: roomsToKeep,
              MinimumForInventoryReflection: minimumForInventoryReflection,
              ReleaseDays: releaseDays,
              AppliedLeftRooms: leftRooms
            }));
        });
        await AllocationAndStrategy.UpdateAllocationDates(allocationDates);

          if (allocation.Distribute && (allocation.HotelLevelAllocation || roomTypes?.length)) {
              if (allocationDates.length > 0) {
                  const dates = allocationDates.map(x => moment(x.Date));
                  const minDate = moment.min(dates);
                  const maxDate = moment.max(dates);
                  loader(true);
                  await SyncChannelManagerProducts({
                      HotelID: selectedHotel,
                      RoomTypeIds: allocation.HotelLevelAllocation ? roomTypesData.map(rt => rt.Id) : roomTypes,
                      RateIds: allocation.RateIds,
                      StartDate: toServerDateFormat(minDate),
                      EndDate: toServerDateFormat(maxDate),
                      IsUpdateAvailabilty: true,
                      RecalculateAvailabilty: true,
                      Reason: `Allocation dates updated, Allocation ID: ${allocation.Id}`,
                      TriggerOperation: "Allocation Update"
                  })
              }
        }

        setIsDirty(false);
        onSuccess();
        onSave?.();
        return true;
      } catch (error) {
        onError(error);
        return false;
      } finally {
        setIsLoading(false);
        loader(false);
      }
    }
    return false;
  };

  const saveAndClose = async () => {
    const ok = await saveAction();
    if (ok) {
      onClose?.();
    }
  };

  const handleClose = async () => {
    if (isDirty) {
      if (await confirm({
        content: `${message("WouldYouLikeToContinue")}?`,
        confirmButton: {
          content: text('Yes'),
          positive: true,
        },
        cancelButton: {
          content: text('No'),
          negative: true,
        },
      }))
        onClose?.();
    }
    else onClose?.();
  };

  const validate = () => {
    // Only compare fields actually present in this submission - an untouched field is never
    // pulled from the DB just to run these checks (server owns the Maximum/TopUp pairing check).
    if (maxRooms != null && allocatedRooms != null && maxRooms < allocatedRooms) {
      onWarning(message("AllocatedRoomsIsBiggerThanMaximumRooms"));
      return false;
    }
    if (topUp != null && maxRooms != null && topUp > maxRooms) {
      onWarning(message("TopUpIsBiggerThanMaximumRooms"));
      return false;
    }

    let missingFields = [];
    if ((!selectedDates || selectedDates.length === 0) && (!dates || !dates[0] || !dates[1])) {
      missingFields.push(text("StartDate"));
      missingFields.push(text("EndDate"));
    }

    if (roomTypes.length === 0 && !allocation.HotelLevelAllocation && !allocation.IsSharedInventory)
      missingFields.push(text("RoomType"));

    if (isEmptyObject(allocation)) {
      missingFields.push(text("Allocation"));
    }

    if (selectedHotel === null) {
      missingFields.push(text("Hotel"));
    }

    if (missingFields.length === 0) {
      return true;
    }

    onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
    return false;
  };

  const addDateToList = () => {
    if (selectedDates.some((d) => moment(d[0]).isSame(dates[0], "day") && moment(d[1]).isSame(dates[1], "day"))) {
      onWarning(text("WarningDuplicateDates"));
      return;
    }
    setSelectedDates([...selectedDates, dates]);
    setIsDirty(true);
  };

  const memoDates = useMemo(() => {
    return selectedDates.length
      ? selectedDates.map((datePair) => ({ startDate: datePair[0], endDate: datePair[1] }))
      : [{ startDate: dates[0], endDate: dates[1] }];
  }, [selectedDates, dates]);

  const handleDatesChanged = (newValue) => {
    if (newValue) {
      setDates(newValue);
    } else {
      const defaultDate = initialDate ? moment.max(moment(initialDate), moment().startOf("day")).toDate() : new Date();
      setDates([defaultDate, defaultDate]);
    }
  }

  const selectedAllocationChanged = async (value) => {
    loader(true);
    var allocationSpread = await AllocationAndStrategy.GetAllocations({ Id: value.Id});

    setAllocation({ ...value,
       RoomTypeIds:[...new Set(allocationSpread.map(a => a.RoomTypeId))],
       RateIds: [...new Set(allocationSpread.map(a => a.RateId))] 
      })

    setSelectedDates([])
    setDates([]);
    setDays([]);
    setRoomTypes([]);
    setAllocatedRooms(null);
    setTopUp(null);
    setRoomsToKeep(null);
    setMinimumForInventoryReflection(null);
    setMaxRooms(null);
    setLeftRooms(null);

    loader(false);
  }

  return (
    <Modal size={isMobileOnly ? "fullscreen" : "tiny"} open={open} onClose={handleClose} className="allocation-dates-modal">
      <Modal.Header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {text("AddAllocationDates")}
      </Modal.Header>
      <Modal.Content style={isMobileOnly ? { overflowY: "auto" } : { maxHeight: 600, overflowY: "auto" }}>
        <Form>
          {!selectedAllocation && (
            <Form.Field
              required
              label={text("Hotel")}
              control={HotelsDropDown}
              onChange={setSelectedHotel}
              spec={hotelsSpec}
              value={selectedHotel}
              search
            />)}
          {!selectedAllocation && (
            <Form.Field
              required
              label={text("Allocation")}
              control={AllocationsDropDown}
              onChangeItem={selectedAllocationChanged}
              spec={allocationsSpec}
              search
            />)}
          <Form.Group unstackable style={{ alignItems: 'flex-end' }}>
            <Form.Field width={12} style={{ display: 'grid' }}>
              <DatePicker picktype='range' onChange={handleDatesChanged} label={text("DateRange")} value={dates} minDate={moment().startOf("day").toDate()} maxDate={maxAvailabilityDate} clearable={false} />
            </Form.Field>
            <Form.Field width={4}>
              <Button size="small" primary onClick={addDateToList} style={{ alignSelf: 'end', marginBottom: '2px' }}>
                {text("AddDate")}
              </Button>
            </Form.Field>
          </Form.Group>
          <Form.Group unstackable widths={"equal"} style={{ flexDirection: "column", margin: "1rem 0" }}>
            {selectedDates.map((selectedDate, index) => (
              <Label.Group key={index} style={{ display: "unset" }}>
                <Label as="a">
                  {moment(selectedDate[0]).format(dateFormat)}-{moment(selectedDate[1]).format(dateFormat)}
                  <Icon
                    key={index}
                    name={"delete"}
                    onClick={() => setSelectedDates(selectedDates.filter((date, dateIndex) => dateIndex !== index))}
                  />
                </Label>
              </Label.Group>
            ))}
            <Message hidden={selectedDates.length === 0} style={{ alignSelf: "baseline" }} info>{message("DaysSelectionForChosenDateRanges")}</Message>
          </Form.Group>
          <Form.Field label={text("Weekdays")} control={WeekDaysSelect} onChange={handleSetDays} dates={memoDates} />
          {!allocation?.HotelLevelAllocation && (
            <Form.Field
              required
              label={text("RoomType")}
              placeholder={text("Select")}
              control={RoomTypesDropDown}
              multiple
              clearable
              onChange={handleSetRoomTypes}
              spec={roomTypeSpec}
              search
              {...(allocation?.IsSharedInventory ? { value: allocation.RoomTypeIds, disabled: true } : {})}
            />
          )}
          {allocation?.InventoryReflectionId !== InventoryReflectionTypesEnum.FullReflectedInventory && <>
            <Form.Group widths={2} unstackable>
              <Form.Field control={NumericInput} label={label("NumberAllocatedRooms", "AllocatedRoomsInformation")} onChange={handleSetAllocatedRooms} min={0} digits={0} value={allocatedRooms} nullable />
              <Form.Field control={NumericInput} label={label("TopUp", "TopUpInformation")} onChange={handleSetTopUp} min={0} digits={0} value={topUp} nullable />
            </Form.Group>
            <Form.Group widths={2} unstackable>
              <Form.Field
                control={NumericInput}
                label={label(`${text("Maximum")} ${text("Rooms")}`, "MaximumRoomsInformation")}
                onChange={handleSetMaxRooms}
                value={maxRooms}
                min={0}
                digits={0}
                nullable
              />
              <Form.Field
                control={NumericInput}
                label={label(text("LeftRooms"), "LeftRoomsInformation")}
                onChange={handleSetLeftRooms}
                value={leftRooms}
                min={0}
                digits={0}
                nullable
              />
            </Form.Group>
            <Form.Group widths={2} unstackable>
              <Form.Field
                control={NumericInput}
                label={label(text("ReleaseDays"), "ReleaseDaysInformation")}
                onChange={handleSetReleaseDays}
                value={releaseDays}
                min={0}
                max={365}
                digits={0}
                nullable
              />
            </Form.Group>
          </>}
          {allocation?.InventoryReflectionId !== InventoryReflectionTypesEnum.NoInventoryReflection &&
            <Form.Group widths={2} unstackable>
              <Form.Field
                label={label("RoomsToKeep", "RoomsToKeepInfo")}
                value={roomsToKeep}
                control={NumericInput}
                onChange={handleSetRoomsToKeep}
                digits={0}
                min={0}
                nullable
              />
              {allocation?.InventoryReflectionId ===
                InventoryReflectionTypesEnum.ReflectInventoryFromSpecifiedValue && (
                  <Form.Field
                    label={text("ReflectInventoryFromMinimumRooms")}
                    value={minimumForInventoryReflection}
                    control={NumericInput}
                    onChange={handleSetMinimumForInventoryReflection}
                    digits={0}
                    min={0}
                    nullable
                  />
                )}
            </Form.Group>}
        </Form>
        <Modal.Actions style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <Button negative onClick={handleClose}>{text('Close')}</Button>
          <Button positive onClick={saveAndClose} loading={isLoading} disabled={isLoading}>{text('Saveandclose')}</Button>
          <Button positive onClick={saveAction} loading={isLoading} disabled={isLoading}>{text('Save')}</Button>
        </Modal.Actions>
      </Modal.Content>
    </Modal>
  );
};

AllocationDatesPopup.propTypes = {
  selectedAllocation: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  hotelId: PropTypes.number,
  isPerReservation: PropTypes.bool,
  initialDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
};
